import { Component, OnInit } from '@angular/core';
import { MatLegacyDialogRef } from "@angular/material/legacy-dialog";
import { CtButtonConfiguration, MAT_RAISED_PRIMARY, MAT_RAISED_WARN } from "@ctsolution/ct-framework";
import { FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';

/**
 * Risultato del dialog di cambio password
 */
export interface UserChangePasswordResult {
  confirmed: boolean;
  oldPassword?: string;
  newPassword?: string;
}

/**
 * Componente per la modale di cambio password utente
 * Permette all'utente di modificare la propria password fornendo:
 * - La vecchia password per conferma identità
 * - La nuova password con requisiti minimi (6 caratteri, almeno un numero)
 */
@Component({
  selector: 'app-user-change-password-dialog',
  templateUrl: './user-change-password-dialog.component.html',
  styleUrls: ['./user-change-password-dialog.component.scss']
})
export class UserChangePasswordDialogComponent implements OnInit {

  /** Form per il cambio password */
  passwordForm!: FormGroup;

  /** Configurazione del pulsante di conferma */
  confirmButton!: CtButtonConfiguration;

  /** Configurazione del pulsante di annullamento */
  cancelButton!: CtButtonConfiguration;

  /** Flag per mostrare/nascondere la vecchia password */
  hideOldPassword: boolean = true;

  /** Flag per mostrare/nascondere la nuova password */
  hideNewPassword: boolean = true;

  constructor(
    private dialogRef: MatLegacyDialogRef<UserChangePasswordDialogComponent>,
    private formBuilder: FormBuilder
  ) {}

  /**
   * Inizializzazione del componente
   * Configura il form e i pulsanti
   */
  ngOnInit(): void {
    this.initializeForm();
    this.initializeButtons();
  }

  /**
   * Inizializza il form con i validatori
   * Validazioni:
   * - Vecchia password: campo obbligatorio
   * - Nuova password: obbligatoria, minimo 6 caratteri, almeno un numero
   */
  private initializeForm(): void {
    this.passwordForm = this.formBuilder.group({
      oldPassword: ['', [Validators.required]],
      newPassword: ['', [
        Validators.required,
        Validators.minLength(6),
        this.passwordValidator
      ]]
    });
  }

  /**
   * Validatore custom per la password
   * Verifica che la password contenga almeno un numero
   * @param control FormControl da validare
   * @returns ValidationErrors | null
   */
  private passwordValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    
    if (!value) {
      return null;
    }

    // Verifica presenza di almeno un numero
    const hasNumber = /[0-9]/.test(value);

    if (!hasNumber) {
      return { noNumber: true };
    }

    return null;
  }

  /**
   * Inizializza i pulsanti con le etichette
   */
  private initializeButtons(): void {
    
    // Configurazione del pulsante di conferma
    this.confirmButton = CtButtonConfiguration
      .create()
      .setLabel('Conferma')
      .setAction(() => this.onConfirm())
      .setMatherialOptions(MAT_RAISED_PRIMARY);

    // Configurazione del pulsante di annullamento
    this.cancelButton = CtButtonConfiguration
      .create()
      .setLabel('Annulla')
      .setAction(() => this.onCancel())
      .setMatherialOptions(MAT_RAISED_WARN);
  }

  /**
   * Gestisce la conferma del cambio password
   * Valida il form e chiude la modale con i dati inseriti
   */
  onConfirm(): void {
    // Marca tutti i campi come touched per mostrare gli errori
    this.passwordForm.markAllAsTouched();

    if (this.passwordForm.valid) {
      const result: UserChangePasswordResult = {
        confirmed: true,
        oldPassword: this.passwordForm.get('oldPassword')?.value,
        newPassword: this.passwordForm.get('newPassword')?.value
      };
      this.dialogRef.close(result);
    }
  }

  /**
   * Gestisce l'annullamento dell'operazione
   * Chiude la modale senza salvare
   */
  onCancel(): void {
    const result: UserChangePasswordResult = {
      confirmed: false
    };
    this.dialogRef.close(result);
  }

  /**
   * Getter per facilitare l'accesso ai controlli del form nel template
   */
  get oldPassword() {
    return this.passwordForm.get('oldPassword');
  }

  /**
   * Getter per facilitare l'accesso ai controlli del form nel template
   */
  get newPassword() {
    return this.passwordForm.get('newPassword');
  }
}
